import { useState } from 'react';
import type { PublicStoreConfig } from '../types';
import { useFeaturedProducts } from '../hooks/useFeaturedProducts';
import { SITE_URL } from '../config';
import platformLogo from '../../public/logo-white.png';

/** Featured thumbnail that hides itself if the image URL fails to load. */
function FeaturedThumb({ src, alt }: { src?: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
    />
  );
}

/** Store logo with a text-initial fallback when the image is missing or broken. */
function StoreLogo({ src, alt, initial }: { src?: string; alt: string; initial: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <>{initial}</>;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className="h-full w-full object-cover"
    />
  );
}

interface Props {
  slug: string;
  config: PublicStoreConfig | null;
  storeName: string;
  storeInitial: string;
  /** Fire a message into the conversation (suggested prompt or category). */
  onPick: (message: string) => void;
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}

// Chat-native discovery defaults. A blank chat asks the shopper to do all the
// work; these give them a one-tap way in. Merchants can override via store config.
const DEFAULT_PROMPTS = [
  'Show me your bestsellers',
  "What's new in?",
  'Help me find a gift',
  "What's on sale?",
];

const DEFAULT_CATEGORIES = [
  'Browse everything',
  'Most popular',
  'New arrivals',
  'Deals & offers',
];

export function WelcomeScreen({ slug, config, storeName, storeInitial, onPick }: Props) {
  const prompts = config?.suggestedPrompts?.length ? config.suggestedPrompts : DEFAULT_PROMPTS;
  const categories = config?.categories?.length ? config.categories : DEFAULT_CATEGORIES;
  const featured = useFeaturedProducts(slug);

  return (
    <div className="animate-pop-in flex flex-col items-center px-2 py-6 text-center">
      {/* Store identity */}
      <div
        className="mb-3 flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl text-xl font-bold text-white shadow-[0_8px_24px_rgba(108,99,255,0.35)]"
        style={{ backgroundColor: 'var(--brand-accent)' }}
      >
        <StoreLogo src={config?.logoUrl} alt={`${storeName} logo`} initial={storeInitial} />
      </div>
      <h2 className="text-base font-bold text-primary">Welcome to {storeName}</h2>
      <p className="mt-1 max-w-xs text-xs leading-relaxed text-mid">
        {config?.storeDescription?.trim() ||
          'Ask anything, get recommendations, and check out — all in one chat.'}
      </p>

      {/* Suggested prompts — the primary "ask" affordance */}
      <div className="mt-5 w-full">
        <p className="mb-2 px-1 text-left text-[11px] font-semibold uppercase tracking-wide text-mid">
          Frequently asked
        </p>
        <div className="flex flex-wrap gap-2">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              onClick={() => onPick(prompt)}
              className="rounded-full border border-border bg-white px-3.5 py-2 text-xs font-medium text-primary shadow-card transition-all hover:-translate-y-0.5 hover:border-[var(--brand-accent)] hover:text-brand active:scale-95"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      {/* Featured products — real photos so shoppers see goods before typing */}
      {featured.length > 0 && (
        <div className="mt-5 w-full">
          <p className="mb-2 px-1 text-left text-[11px] font-semibold uppercase tracking-wide text-mid">
            Popular right now
          </p>
          <div className="scroll-smooth-x -mx-1 flex gap-2.5 overflow-x-auto px-1 pb-1">
            {featured.map((product) => (
              <button
                key={product.id}
                onClick={() => onPick(`Tell me more about ${product.name}`)}
                aria-label={`View ${product.name}`}
                className="group w-32 shrink-0 overflow-hidden rounded-md border border-border bg-white text-left shadow-card transition-all hover:-translate-y-0.5 hover:border-[var(--brand-accent)] active:scale-[0.98]"
              >
                <div className="aspect-square w-full overflow-hidden bg-surface">
                  <FeaturedThumb src={product.imageUrl} alt={product.name} />
                </div>
                <div className="flex flex-col gap-0.5 p-2">
                  <span className="truncate text-xs font-medium text-primary">{product.name}</span>
                  <span className="text-xs font-bold text-primary">
                    {formatPrice(product.price, product.currency)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Shop by category — a path in for shoppers who won't type */}
      <div className="mt-5 w-full">
        <p className="mb-2 px-1 text-left text-[11px] font-semibold uppercase tracking-wide text-mid">
          Shop by category
        </p>
        <div className="flex flex-col gap-1.5">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => onPick(`Show me ${category.toLowerCase()}`)}
              className="group flex items-center justify-between rounded-md border border-border bg-white px-3.5 py-3 text-left text-sm font-medium text-primary shadow-card transition-all hover:-translate-y-0.5 hover:border-[var(--brand-accent)] active:scale-[0.99]"
            >
              <span>{category}</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-mid transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          ))}
        </div>
      </div>

      {/* Powered-by mark — light surface, so the dark logo reads best. */}
      <a
        href={SITE_URL}
        className="mt-6 flex items-center gap-1.5 text-[11px] font-medium text-mid opacity-70 transition-opacity hover:opacity-100"
      >
        <span>Powered by</span>
        <img src={platformLogo} alt="Cartiq" className="h-4 w-auto" />
      </a>
    </div>
  );
}

export default WelcomeScreen;
